import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { BaseComponent } from '@library-shared/base/base.component';
import { Router, ActivatedRoute } from '@angular/router';
import { EventBusService } from '@library-shared/services/event-bus/event-bus.service';
import { CookieService } from 'ngx-cookie-service';
import validationEngine from 'devextreme/ui/validation_engine';
import { UserRoleSelectComponent } from './user-role-select/user-role-select.component';
import { DisplayMode } from '@constant/display-mode.constant';
import { vUserX } from '@models/Views-Extended/vUserX.extended';
import { GlobalService } from '@services/global.service';

import { DxButtonModule, DxCheckBoxModule, DxSelectBoxModule, DxTextAreaModule, DxTextBoxModule, DxToolbarModule, DxValidationSummaryComponent, DxValidationSummaryModule, DxValidatorModule } from 'devextreme-angular';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { UserService } from '@services/user/user.service';
import { spAgency_List } from '@models-surebank/StoredProcs/spAgency_List';
import { AgencyService } from '@services/agency/agency.service';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';
import { DxiItemModule, DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxTextBoxComponent } from 'devextreme-angular';
import { ConfirmationDialogComponent } from '@library-shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { TtiCheckBoxComponent } from '@library-shared/form/tti-check-box/tti-check-box.component';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    imports: [DxiItemModule, DxButtonModule, ConfirmationDialogComponent, DxValidationSummaryModule, UserRoleSelectComponent, DxTextAreaModule, DxTextBoxModule,
        TtiCheckBoxComponent, DxiValidationRuleModule, DxValidatorModule, DxSelectBoxModule, DxiValidationRuleModule, DxCheckBoxModule, NgxUiLoaderModule, DxToolbarModule]
})
export class UserEditComponent extends BaseComponent implements OnInit, AfterViewInit {
    get canAddOrEdit(): boolean {
        return this.displayMode === DisplayMode.AddMode || this.displayMode === DisplayMode.EditMode;
    }

    get canAdd(): boolean {
        return this.displayMode === DisplayMode.AddMode;
    }

    get canEdit(): boolean {
        return this.displayMode === DisplayMode.EditMode;
    }

    get agency_name(): string {
        return this.agencyList.find((r) => r.gAgencyID === this.user?.agency_id)?.sName;
    }

    @ViewChild('roleSelect') roleSelect: UserRoleSelectComponent;
    @ViewChild('validationSummary', { static: true }) validationSummary: DxValidationSummaryComponent;

    displayMode = DisplayMode.DisplayMode;
    private routeSub: any;
    user_id = '';
    user: vUserX = new vUserX();
    authenticatedUser = new vUserX();
    showPasswordResetConfirmation = false;
    agencyList: spAgency_List[] = [];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private userService: UserService,
        private globalService: GlobalService,
        private authenticationService: AuthenticationService,
        private agencyService: AgencyService,
        private loaderService: NgxUiLoaderService
    ) {
        super();
    }

    ngOnInit(): void {
        this.loaderService.startLoader(this.componentID);
        this.isLoading = true;

        this.routeSub = this.route.params.subscribe((params) => {
            this.user_id = params.id;
            this.loadData();
        });
    }

    ngAfterViewInit(): void {
        this.globalService.AuthenticatedUser().subscribe((user) => {
            if (user) {
                this.authenticatedUser = user;
            } else {
                this.authenticatedUser = new vUserX();
            }
        });
    }

    loadData() {
        this.isLoading = true;
        this.loaderService.startLoader(this.componentID);

        this.agencyService.Agency_List().subscribe({
            next: (data) => {
                this.agencyList = data.records;

                if (this.user_id !== GlobalService.zeroGuid) {
                    this.userService.UserView_ByUserID(this.user_id).subscribe((data2) => {
                        this.user = data2.record;
                        this.isLoading = false;
                        this.loaderService.stopLoader(this.componentID);
                        this.roleSelect?.loadData();
                    });
                } else {
                    this.user = new vUserX();
                    this.user.is_active = true;
                    this.user.user_id = GlobalService.zeroGuid;
                    this.isLoading = false;
                    this.loaderService.stopLoader(this.componentID);
                    this.displayMode = DisplayMode.EditMode;
                }
            },
        });
    }

    onEdit() {
        this.displayMode = DisplayMode.EditMode;
    }

    saveChanges(e: any) {
        const valid = validationEngine.validateGroup(this.validationGroup);

        if (valid.isValid) {
            this.isLoading = true;
            this.userService.User_Validate(this.user).subscribe((data) => {
                if (data.records.length === 0) {
                    const roleList = this.roleSelect.save();

                    this.userService.User_Update(this.user, roleList).subscribe((data2) => {
                        if (data2.success) {
                            this.toast_info({ message: 'User Saved' });
                            this.displayMode = DisplayMode.DisplayMode;
                            this.router.navigateByUrl(`/user/edit/${data2.record.user_id}`);
                        } else {
                            this.toast_error({ message: 'There was an issue saving the user information' });
                        }
                        this.isLoading = false;
                    });
                } else {
                    const itemList: string[] = [];
                    data.records.forEach((i) => {
                        itemList.push(i.sMessage);
                    });
                    this.validationSummary.instance.option('items', itemList);
                    this.isLoading = false;
                }
            });
        }
    }

    cancelChanges(e: any) {
        if (this.user_id !== GlobalService.zeroGuid) {
            this.roleSelect.cancel();
            this.displayMode = DisplayMode.DisplayMode;
            this.loadData();
        } else {
            this.router.navigateByUrl('/user/manager');
        }
    }

    goUserManager() {
        this.router.navigateByUrl(`/user/manager`);
    }

    passwordReset() {
        this.showPasswordResetConfirmation = true;
    }

    onPasswordResetConfirmed(confirmed: boolean) {
        this.showPasswordResetConfirmation = false;
        if (confirmed) {
            this.isLoading = true;
            this.authenticationService.forgotPassword(this.user.email).subscribe((data) => {
                this.isLoading = false;
                if (data.success) {
                    this.toast_info({ message: 'An email has been sent to the address' });
                } else {
                    this.toast_error({ message: 'There was a problem sending out the email' });
                }
            });
        }
    }
}
